import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get("error");
    if (err === "ms_failed") toast.error("Échec de la connexion Microsoft 365. Vérifiez la configuration Azure.");
    else if (err === "no_email") toast.error("Aucune adresse email retournée par Microsoft 365.");
    else if (err === "session") toast.error("Session invalide, veuillez réessayer.");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.access_token, data.user);
      toast.success(`Bienvenue ${data.user.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#001A3B] p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1616363088386-31c4a8414858?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#001A3B]/70 to-[#001A3B]" />
        <div className="relative">
          <Logo variant="dark" size={72} />
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-bold leading-tight text-white">
            Pilotez votre flotte<br />de véhicules en temps réel.
          </h1>
          <p className="mt-4 max-w-md text-[#9FB2CE]">
            La plateforme centralisée de gestion de flotte du groupe Omer-Decugis &amp; Cie : véhicules, conducteurs, contrats, échéances et coûts, réunis en un seul endroit.
          </p>
        </div>
        <div className="relative text-xs text-[#6B82A5]">© 2026 Omer-Decugis &amp; Cie — Gestion de flotte interne</div>
      </div>

      {/* Right login form */}
      <div className="flex w-full items-center justify-center bg-[#F5F7FA] p-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><Logo size={64} /></div>
          <h2 className="font-heading text-2xl font-bold text-[#18221F]">Connexion</h2>
          <p className="mt-1 text-sm text-[#4A5568]">Accédez à votre espace de gestion de flotte.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="mb-1.5 block text-sm">Adresse email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white" data-testid="login-email" placeholder="prenom.nom@omerdecugis.com" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Mot de passe</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-white" data-testid="login-password" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#A5894E] hover:bg-[#8F7440]" data-testid="login-submit">
              {loading ? "Connexion…" : <>Se connecter <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#E2E8F0]" />
            <span className="text-xs text-[#8695AB]">ou</span>
            <div className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <button
            type="button"
            onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`; }}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-[#E2E8F0] bg-white py-2.5 text-sm font-medium text-[#18221F] transition-colors hover:bg-[#F5F7FA]"
            data-testid="login-microsoft"
          >
            <svg className="h-4 w-4" viewBox="0 0 23 23"><path fill="#f35325" d="M1 1h10v10H1z" /><path fill="#81bc06" d="M12 1h10v10H12z" /><path fill="#05a6f0" d="M1 12h10v10H1z" /><path fill="#ffba08" d="M12 12h10v10H12z" /></svg>
            Se connecter avec Microsoft 365
          </button>
        </div>
      </div>
    </div>
  );
}
